import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Inbox, Trash2, Mail, Phone, User, Building2, Briefcase, MapPin, Globe, ScanLine, MessageSquare, Tag, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', className: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  { value: 'contacted', label: 'Contacted', className: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  { value: 'hot', label: 'Hot', className: 'bg-orange-500/15 text-orange-300 border-orange-500/30' },
  { value: 'follow_up', label: 'Follow Up', className: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  { value: 'closed_won', label: 'Closed Won', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  { value: 'cancelled', label: 'Cancelled', className: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' },
];

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'scanner', label: 'Scanned' },
  { value: 'contact_form', label: 'Contact Form' },
  { value: 'event_rsvp', label: 'Event RSVP' },
];

const getLeadStatus = (lead) => lead.status || 'new';
const getStatusOption = (status) => STATUS_OPTIONS.find(option => option.value === status) || STATUS_OPTIONS[0];
const getSourceLabel = (source) => SOURCE_OPTIONS.find(option => option.value === source)?.label || 'Other';

const LeadsInbox = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingLeadIds, setUpdatingLeadIds] = useState([]);

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads`);
      setLeads(res.data);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`);
      setLeads(currentLeads => currentLeads.filter(lead => lead.id !== id));
      toast.success('Lead deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleStatusChange = async (leadId, status) => {
    const previousLead = leads.find(lead => lead.id === leadId);
    if (!previousLead || getLeadStatus(previousLead) === status) return;

    setUpdatingLeadIds(currentIds => [...currentIds, leadId]);
    setLeads(currentLeads => currentLeads.map(lead => (
      lead.id === leadId ? { ...lead, status } : lead
    )));

    try {
      const response = await axios.patch(`${API}/leads/${leadId}/status`, { status });
      setLeads(currentLeads => currentLeads.map(lead => (
        lead.id === leadId ? response.data : lead
      )));
      toast.success(`Lead marked as ${getStatusOption(status).label}`);
    } catch (err) {
      setLeads(currentLeads => currentLeads.map(lead => (
        lead.id === leadId ? previousLead : lead
      )));
      toast.error(err.response?.data?.detail || 'Failed to update lead status');
    } finally {
      setUpdatingLeadIds(currentIds => currentIds.filter(id => id !== leadId));
    }
  };

  const filtered = leads.filter(lead => (
    (sourceFilter === 'all' || lead.source === sourceFilter)
    && (statusFilter === 'all' || getLeadStatus(lead) === statusFilter)
  ));

  return (
    <div className="max-w-4xl" data-testid="leads-inbox">
      <div className="mb-6">
        <h1 className="font-outfit font-bold text-2xl text-white">Leads</h1>
        <p className="text-[#A1A1AA] mt-1 text-sm">Manage contacts from your scanner, contact form, and event RSVPs</p>
      </div>

      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#71717A] mr-1">Source</span>
          {SOURCE_OPTIONS.map(option => {
            const count = option.value === 'all'
              ? leads.length
              : leads.filter(lead => lead.source === option.value).length;
            return (
              <button
                key={option.value}
                data-testid={`source-filter-${option.value}`}
                onClick={() => setSourceFilter(option.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  sourceFilter === option.value
                    ? 'bg-violet-600 text-white'
                    : 'bg-[#1E1E1E] text-[#A1A1AA] hover:text-white border border-[#27272A]'
                }`}
              >
                {option.label} ({count})
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-[#71717A] mr-1">Status</span>
          <button
            data-testid="status-filter-all"
            onClick={() => setStatusFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-white text-black'
                : 'bg-[#1E1E1E] text-[#A1A1AA] hover:text-white border border-[#27272A]'
            }`}
          >
            All ({leads.length})
          </button>
          {STATUS_OPTIONS.map(option => {
            const count = leads.filter(lead => getLeadStatus(lead) === option.value).length;
            return (
              <button
                key={option.value}
                data-testid={`status-filter-${option.value}`}
                onClick={() => setStatusFilter(option.value)}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                  statusFilter === option.value
                    ? option.className
                    : 'bg-[#1E1E1E] text-[#A1A1AA] border-[#27272A] hover:text-white'
                }`}
              >
                {option.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((lead, i) => (
            <motion.div
              key={lead.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="rounded-xl bg-[#121212] border border-[#27272A] p-5 hover:border-[#3f3f46] transition-colors"
            >
              <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                <div className="flex-1 min-w-0 w-full">
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                      lead.source === 'scanner' ? 'bg-teal-500/15' : 'bg-violet-500/15'
                    }`}>
                      {lead.source === 'scanner'
                        ? <ScanLine className="w-4 h-4 text-teal-400" />
                        : <User className="w-4 h-4 text-violet-400" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-white text-base">{lead.fields?.name || lead.name || 'Unknown'}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          lead.source === 'scanner'
                            ? 'bg-teal-500/15 text-teal-400'
                            : 'bg-violet-500/15 text-violet-400'
                        }`}>
                          {getSourceLabel(lead.source)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-[#525252] mt-0.5 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {lead.location_name && (
                          <span className="flex items-center gap-1 text-amber-400/70">
                            <Tag className="w-3 h-3" />
                            {lead.location_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 text-sm text-[#A1A1AA] ml-12 mb-2">
                    {(lead.fields?.email || lead.email) && (
                      <span className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" />{lead.fields?.email || lead.email}
                      </span>
                    )}
                    {(lead.fields?.phone || lead.fields?.contact || lead.phone) && (
                      <span className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5" />{lead.fields?.phone || lead.fields?.contact || lead.phone}
                      </span>
                    )}
                  </div>

                  {lead.source === 'scanner' && (
                    <div className="ml-12 mt-2 p-3 rounded-lg bg-white/[0.03] border border-white/[0.05] space-y-1.5">
                      {lead.fields?.company && (
                        <p className="text-sm text-[#a1a1aa] flex items-center gap-1.5">
                          <Building2 className="w-3.5 h-3.5 text-[#71717A]" /> {lead.fields.company}
                        </p>
                      )}
                      {lead.fields?.job_title && (
                        <p className="text-sm text-[#a1a1aa] flex items-center gap-1.5">
                          <Briefcase className="w-3.5 h-3.5 text-[#71717A]" /> {lead.fields.job_title}
                        </p>
                      )}
                      {lead.fields?.address && (
                        <p className="text-sm text-[#a1a1aa] flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-[#71717A]" /> {lead.fields.address}
                        </p>
                      )}
                      {lead.fields?.website && (
                        <p className="text-sm text-[#a1a1aa] flex items-center gap-1.5">
                          <Globe className="w-3.5 h-3.5 text-[#71717A]" />
                          <a href={lead.fields.website.startsWith('http') ? lead.fields.website : `https://${lead.fields.website}`}
                            target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:underline">
                            {lead.fields.website}
                          </a>
                        </p>
                      )}
                    </div>
                  )}

                  {lead.source !== 'scanner' && lead.fields && (
                    <div className="ml-12 mt-2 space-y-1">
                      {Object.entries(lead.fields).filter(([k]) => !['name', 'email', 'contact', 'phone'].includes(k)).map(([key, value]) => (
                        value && <p key={key} className="text-sm text-[#a1a1aa]"><span className="text-[#71717A] capitalize">{key.replace(/_/g, ' ')}:</span> {value}</p>
                      ))}
                    </div>
                  )}

                  {!lead.fields && lead.message && (
                    <div className="ml-12 mt-2 p-3 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                      <p className="text-sm text-[#a1a1aa] leading-relaxed flex items-center gap-1.5">
                        <MessageSquare className="w-3.5 h-3.5 text-[#71717A] flex-shrink-0" /> {lead.message}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
                  <select
                    aria-label={`Status for ${lead.fields?.name || lead.name || 'lead'}`}
                    data-testid={`lead-status-${lead.id}`}
                    value={getLeadStatus(lead)}
                    onChange={event => handleStatusChange(lead.id, event.target.value)}
                    disabled={updatingLeadIds.includes(lead.id)}
                    className={`h-8 rounded-full border px-2.5 text-xs font-semibold outline-none cursor-pointer disabled:cursor-wait disabled:opacity-60 ${
                      getStatusOption(getLeadStatus(lead)).className
                    }`}
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option key={option.value} value={option.value} className="bg-[#121212] text-white">
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    title="Delete lead"
                    aria-label={`Delete ${lead.fields?.name || lead.name || 'lead'}`}
                    data-testid={`delete-lead-${lead.id}`}
                    onClick={() => handleDelete(lead.id)}
                    className="p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-[#525252] hover:text-red-400" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="text-center py-20 rounded-2xl bg-[#121212] border border-[#27272A]">
          <div className="w-14 h-14 rounded-full bg-[#1E1E1E] flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-7 h-7 text-[#525252]" />
          </div>
          <h3 className="font-outfit font-semibold text-lg text-white mb-2">
            {leads.length === 0 ? 'No leads yet' : 'No matching leads'}
          </h3>
          <p className="text-[#71717A] text-sm max-w-xs mx-auto">
            {leads.length === 0
              ? 'Scan business cards or share your contact form to collect leads'
              : 'Try choosing a different source or status filter'}
          </p>
        </div>
      )}
    </div>
  );
};

export default LeadsInbox;
